import type { SliceErrorState } from '@/store/slices/sliceError';

export type GameSlice = {
  /** Increments whenever {@link resetGamePart} or {@link resetAllGameParts} runs (for effects that should react to game store resets). */
  gameResetRevision: number;
  gameContext: GameContext;
  gameStatus: GameStatus;
  clientStatus: ClientStatus;
  /** GLS init payload and handshake-gated delivery state for GL/INITIALIZE_URL. */
  gamePayload: GamePayload;
  /** Root-level name avoids clashing with other slices' `error` when merging */
  gameError: SliceErrorState;
  setGameContext: (newState: GameContextPayload) => void;
  setGameStatus: (newState: GameStatusPayload) => void;
  setClientStatus: (newState: ClientStatusPayload) => void;
  setGamePayload: (newState: GamePayloadPayload) => void;
  setGameError: (newState: GameErrorPayload) => void;
  resetGamePart: (
    part: GameStoreParts,
    options?: ResetAllGamePartsOptions,
  ) => void;
  resetAllGameParts: (options?: ResetAllGamePartsOptions) => void;
};

/** Selective fields kept on {@link resetAllGameParts} (e.g. launch-new-game soft reload). */
export type ResetAllGamePartsOptions = {
  skipRevisionBump?: boolean;
  preserveGamePayloadKeys?: readonly (keyof GamePayload)[];
  preserveClientStatusKeys?: readonly (keyof ClientStatus)[];
  /**
   * When set, payload/client preserves apply only if `gamePayload.handshakeLaunchEpoch`
   * equals this value (typically `getGlsLaunchGeneration()` after invalidation).
   */
  launchHandshakeEpochGate?: number;
};

export enum GameStoreParts {
  gameContext = 'gameContext',
  gameStatus = 'gameStatus',
  clientStatus = 'clientStatus',
  gamePayload = 'gamePayload',
  gameError = 'gameError',
}

/** GLS init URL and delivery handshake state (launch epoch lives in gls-launch-guard). */
export type GamePayload = {
  glsInitializeUrl: unknown | null;
  /** Launch epoch recorded at the last GC/INITIALIZE_HANDSHAKE. */
  handshakeLaunchEpoch: number | null;
};

export type GamePayloadPayload = Partial<GamePayload>;

export type GameContextPayload = Partial<GameContext>;
export type GameStatusPayload = Partial<GameStatus>;
export type ClientStatusPayload = Partial<ClientStatus>;
export type GameErrorPayload = Partial<SliceErrorState>;

export type LaunchGeoProduct = 'CASINO' | 'POKER' | 'UNKNOWN';

export type GameContext = {
  rgi: string | null;
  brand: string | null;
  gameName: string | null;
  gameId: string | null;
  provider: string | null;
  /** Provider string from the first successful GLS init; not overwritten by aggregation. */
  glsLaunchProvider: string | null;
  providerUid: string | null;
  providerRef: string | null;
  gameUid: string | null;
  /** Game studio identifier (from aggregation `game_studio.uid` or GLS `gameData.gameStudio`); telemetry only. */
  gameStudio: string | null;
  region: string | null;
  directLaunchParams: Record<string, unknown> | null;
  /** Provider-level flag (`provider.show_net_position`) gating the CA net-position readout. */
  showNetPosition: boolean | null;
  /** Prismic game-management flag (`gameInfo.unpausable`) marking time-sensitive games that can't be paused; consumers gate pausing UI (e.g. in-game-merch toast/modal) on it. */
  unpausable: boolean | null;
  /** Game superclass uid (e.g. `superclass-slots`) used by the net-position feature gate. */
  superclass: string | null;
  /** How the current game was launched (`traditional-iframe` or `unified-module`); hydrated from sessionStorage on load. */
  launchMethod: string;
  gamingSessionTrackingId?: string | null;
  /** Request ID returned by the successful GLS init and required for session-scoped updates. */
  glsRequestId: string | null;
  isShortLinkLaunch: boolean;
  /** Product for which the game launch geolocation token must be usable. */
  launchGeoProduct: LaunchGeoProduct;
  /** Prevents a GLS product mismatch from causing an unbounded locate/retry loop. */
  geoProductRetryAttempted: boolean;
};

export type GameStatus = {
  gameFetchStarted: boolean;
  gameFetchSuccess: boolean;
  gameFetchFailed: boolean;
  gameFetchErrorCode: string | null;
  gameLoadStarted: boolean;
  gameLoadSuccess: boolean;
  gameLoadProgress: number;
  internalLoadProgress: number;
  // Hidden testing flag
  hiddenTestingFlags: string[] | null;

  /** Game `status_overall` from Prismic (via aggregation); `null` until resolved. */
  overallStatus: string | null;

  // Overall maintenance mode
  maintenanceMode: boolean;

  forceSwjBar: boolean;

  latestAuthenticationTokenUpdated: string | null;

  /** Prevents duplicate GLS fetches while jackpot GLS reconcile runs. */
  jackpotReconcileRunning: boolean;
  /**
   * Sole latch for auto jackpot GLS recon: true after GC/CONTEXT_RECEIVED for the
   * prewarm initialize URL (while launch vitals are in prewarm). Reset on silent relaunch.
   */
  jackpotGlsPrewarmContextReceived: boolean;
};

export type ClientStatus = {
  /** GC posted the initialize handshake for the current launch; not iframe DOM `onLoad`. */
  loaded: boolean;
};

export type GameError = SliceErrorState;
